"use client"

import * as React from "react"
import { OpenAppTab, App } from "@/types/workspace"
import { Calendar } from "@/components/ui/calendar"
import { WeatherWidget } from "@/components/widgets/WeatherWidget"
import { CalendarApp } from "@/components/widgets/CalendarApp"
import { NotesApp } from "@/components/widgets/NotesApp"
import { CalculatorApp } from "@/components/widgets/CalculatorApp"
import { SettingsView } from "@/components/settings/SettingsView"
import { ProfileView } from "@/components/profile/ProfileView"
import { MarketplaceView } from "@/components/marketplace/MarketplaceView"
import { NewTabView } from "@/components/desk/NewTabView"
import { getAppSlug, isAppMatch, resolveIframeUrl, buildWorkspaceAppPath, getWorkspaceBasePath } from "@/hooks/useAppRouterSync"
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "@/components/ui/resizable"
import { AppIcon } from "@/components/desk/AppIcon"
import { Button } from "@/components/ui/button"
import { Rows, Columns, ChevronDown, Minus, PanelRightClose, X, Split } from "lucide-react"
import { PinnedWidgetsPanel, PanelSide } from "@/components/desk/PinnedWidgetsPanel"

interface AppViewportContainerProps {
  activeAppId: string | null
  setActiveAppId: (id: string | null) => void
  splitActiveAppId: string | null
  setSplitActiveAppId: (id: string | null) => void
  focusedPane: "left" | "right"
  setFocusedPane: (pane: "left" | "right") => void
  splitOrientation: "horizontal" | "vertical"
  setSplitOrientation: (orientation: "horizontal" | "vertical") => void
  splitProportion: number
  setSplitProportion: (prop: number) => void
  openApps: OpenAppTab[]
  setOpenApps: React.Dispatch<React.SetStateAction<OpenAppTab[]>>
  installedApps: any[]
  iframeRefreshKeys: Record<string, number>
  activeFloatingWidgets?: string[]
  pinnedWidgets: string[]
  widgetsConfig: any[]
  minimizedWidgets: Set<string>
  calcFocusedWidgetId: string | null
  setCalcFocusedWidgetId: (id: string | null) => void
  handleMinimizeWidget: (id: string) => void
  handlePinWidget: (id: string) => void
  handleToggleFloatingWidget: (id: string) => void
  handleInstallApp?: (app: any) => void
  calendarDate: Date | undefined
  setCalendarDate: (date: Date | undefined) => void
  calcFormula: string
  setCalcFormula: React.Dispatch<React.SetStateAction<string>>
  calcDisplay: string
  setCalcDisplay: React.Dispatch<React.SetStateAction<string>>
  showCalcHistory: boolean
  setShowCalcHistory: React.Dispatch<React.SetStateAction<boolean>>
  calcHistory: any[]
  setCalcHistory: React.Dispatch<React.SetStateAction<any[]>>
  handleCalcPress: (val: string) => void
  setCalcResetOnNext: React.Dispatch<React.SetStateAction<boolean>>
  notesContent: string
  setNotesContent: React.Dispatch<React.SetStateAction<string>>
  isMobile: boolean
  settingsTab: any
  setSettingsTab: (tab: any) => void
  t: any
  profileRole: string
  workspaceRole?: string
  userWorkspaceMemberships?: Array<{ workspaceId: string; role: string }>
  adminWorkspaces?: any[]
  currentAccount: any
  currentWorkspace?: any
  handleConfigureAppWorkspaces?: (appId: string, workspaceIds: string[]) => Promise<void>
  handleSaveCompany: any
  companyName: string
  setCompanyName: any
  companyDocument: string
  setCompanyDocument: any
  companyLogoUrl: string
  setCompanyLogoUrl: any
  companyBannerUrl?: string
  setCompanyBannerUrl?: any
  companyPrimaryColor: string
  setCompanyPrimaryColor: any
  companyCustomDomain: string
  setCompanyCustomDomain: any
  companySamlEnabled: boolean
  setCompanySamlEnabled: any
  companySamlIdpEntityId: string
  setCompanySamlIdpEntityId: any
  companySamlSsoUrl: string
  setCompanySamlSsoUrl: any
  companySamlCertificate: string
  setCompanySamlCertificate: any
  accounts: any[]
  handleSwitchWorkspace: any
  handleCreateWorkspace?: any
  handleUpdateWorkspace?: any
  handleArchiveWorkspace?: any
  handleReactivateWorkspace?: any
  handleDeleteWorkspace?: any
  usersList: any[]
  handleInviteUser: any
  handleUpdateAccountMember?: any
  handleUpdateUserRole?: any
  handleRemoveUser: any
  wallpaperTab: any
  setWallpaperTab: any
  mobileWallpaper: string
  setMobileWallpaper: any
  desktopWallpaper: string
  setDesktopWallpaper: any
  customWallpapers?: string[]
  setCustomWallpapers?: (urls: string[]) => void
  useSeparateMobileWallpaper: boolean
  setUseSeparateMobileWallpaper: any
  handleSaveWallpaperToDb: any
  dockPosition: any
  setDockPosition: any
  dockSize: number
  setDockSize: any
  dockAppIds?: string[]
  setDockAppIds?: React.Dispatch<React.SetStateAction<string[]>>
  dockAutoHide: boolean
  setDockAutoHide: any
  topBarConfig: any
  setTopBarConfig: any
  handleToggleAppStatus: any
  handleUninstallApp: any
  handleRenameApp?: any
  availableWidgets: any[]
  setWidgetsConfig?: React.Dispatch<React.SetStateAction<any[]>>
  handleToggleWidgetPin: any
  marketplaceApps: App[]
  globallyInstalledApps: string[]
  developerApps: any[]
  onAddCustomApp?: (app: App, isEdit?: boolean) => Promise<void> | void
  setAuthAppToShow: any
  handleToggleGlobalApp: any
  handleToggleGlobalWidget?: any
  apiKey: string
  handleGenerateApiKey: any
  webhookUrl: string
  setWebhookUrl: any
  handleSaveWebhook: any
  handleOpenMarketplaceTab?: () => void
  handleOpenApp?: (app: any) => void
  handleOpenExternalUrl?: (url: string, options?: { forceNewTab?: boolean; inBackground?: boolean; openInSplit?: boolean; sourceBaseUrl?: string }) => void
  hiddenAppIds?: string[]
  setHiddenAppIds?: React.Dispatch<React.SetStateAction<string[]>>
  devSubTab?: string
  setDevSubTab?: (subTab: string) => void
  desktopGridCols?: number
  setDesktopGridCols?: (cols: number) => void
  desktopIconSize?: number
  setDesktopIconSize?: (size: number) => void
  desktopShowLabels?: boolean
  setDesktopShowLabels?: (show: boolean) => void
  lang: any
  theme?: any
  user: any
  profileTab: any
  setProfileTab: any
  profileName: string
  setProfileName: any
  profilePhone: string
  setProfilePhone: any
  profileAvatarUrl: string | null
  setProfileAvatarUrl: any
  handleSaveProfile: any
  isLoginMusicEnabled: boolean
  setIsLoginMusicEnabled: any
  loginMusicUrl: string
  setLoginMusicUrl: any
  fullSizeScreen?: boolean
  setFullSizeScreen?: any
  currentPassword: string
  setCurrentPassword: any
  newPassword: string
  setNewPassword: any
  confirmPassword: string
  setConfirmPassword: any
  isChangingPassword: boolean
  handleSavePassword: any
  totpDisableCode: string
  setTotpDisableCode: any
  handleDisable2FA: any
  handleEnable2FA: any
  setupData: any
  totpError: string | null
  totpCode: string
  setTotpCode: any
  lockscreenPin: string
  setLockscreenPin: any
  handleCloseApp: (id: string) => void
  weatherInfo: string
  pinnedWidgetSides: Record<string, PanelSide>
  setPinnedWidgetSides: React.Dispatch<React.SetStateAction<Record<string, PanelSide>>>
  collapsedSides: Record<PanelSide, boolean>
  setCollapsedSides: React.Dispatch<React.SetStateAction<Record<PanelSide, boolean>>>
  sideWidths: Record<"left" | "right", number>
  setSideWidths: React.Dispatch<React.SetStateAction<Record<"left" | "right", number>>>
  topPanelHeight: number
  setTopPanelHeight: React.Dispatch<React.SetStateAction<number>>
  showToast?: (title: string, desc: string, variant?: any) => void
  setPinnedWidgets?: React.Dispatch<React.SetStateAction<string[]>>
  updateAppSubpath?: (instanceId: string, subpath: string) => void
}

export function AppViewportContainer({
  activeAppId,
  setActiveAppId,
  splitActiveAppId,
  setSplitActiveAppId,
  focusedPane,
  setFocusedPane,
  splitOrientation,
  setSplitOrientation,
  splitProportion,
  setSplitProportion,
  openApps,
  setOpenApps,
  installedApps,
  iframeRefreshKeys,
  activeFloatingWidgets = [],
  pinnedWidgets,
  widgetsConfig,
  minimizedWidgets,
  calcFocusedWidgetId,
  setCalcFocusedWidgetId,
  updateAppSubpath,
  handleMinimizeWidget,
  handlePinWidget,
  handleToggleFloatingWidget,
  calendarDate,
  setCalendarDate,
  calcFormula,
  setCalcFormula,
  calcDisplay,
  setCalcDisplay,
  showCalcHistory,
  setShowCalcHistory,
  calcHistory,
  setCalcHistory,
  handleCalcPress,
  setCalcResetOnNext,
  notesContent,
  setNotesContent,
  isMobile,
  settingsTab,
  setSettingsTab,
  t,
  profileRole,
  workspaceRole = "member",
  userWorkspaceMemberships = [],
  adminWorkspaces,
  currentAccount,
  currentWorkspace,
  handleConfigureAppWorkspaces,
  handleSaveCompany,
  companyName,
  setCompanyName,
  companyDocument,
  setCompanyDocument,
  companyLogoUrl,
  setCompanyLogoUrl,
  companyBannerUrl,
  setCompanyBannerUrl,
  companyPrimaryColor,
  setCompanyPrimaryColor,
  companyCustomDomain,
  setCompanyCustomDomain,
  companySamlEnabled,
  setCompanySamlEnabled,
  companySamlIdpEntityId,
  setCompanySamlIdpEntityId,
  companySamlSsoUrl,
  setCompanySamlSsoUrl,
  companySamlCertificate,
  setCompanySamlCertificate,
  accounts,
  handleSwitchWorkspace,
  handleCreateWorkspace,
  handleUpdateWorkspace,
  handleArchiveWorkspace,
  handleReactivateWorkspace,
  handleDeleteWorkspace,
  usersList,
  handleInviteUser,
  handleUpdateAccountMember,
  handleUpdateUserRole,
  handleRemoveUser,
  wallpaperTab,
  setWallpaperTab,
  mobileWallpaper,
  setMobileWallpaper,
  desktopWallpaper,
  setDesktopWallpaper,
  customWallpapers,
  setCustomWallpapers,
  useSeparateMobileWallpaper,
  setUseSeparateMobileWallpaper,
  handleSaveWallpaperToDb,
  dockPosition,
  setDockPosition,
  dockSize,
  setDockSize,
  dockAppIds,
  setDockAppIds,
  dockAutoHide,
  setDockAutoHide,
  topBarConfig,
  setTopBarConfig,
  handleToggleAppStatus,
  handleUninstallApp,
  handleInstallApp,
  handleRenameApp,
  availableWidgets,
  setWidgetsConfig,
  handleToggleWidgetPin,
  marketplaceApps,
  globallyInstalledApps,
  developerApps,
  onAddCustomApp,
  setAuthAppToShow,
  handleToggleGlobalApp,
  handleToggleGlobalWidget,
  apiKey,
  handleGenerateApiKey,
  webhookUrl,
  setWebhookUrl,
  handleSaveWebhook,
  handleOpenMarketplaceTab,
  handleOpenApp,
  handleOpenExternalUrl,
  hiddenAppIds = [],
  setHiddenAppIds,
  devSubTab,
  setDevSubTab,
  lang,
  theme,
  user,
  profileTab,
  setProfileTab,
  profileName,
  setProfileName,
  profilePhone,
  setProfilePhone,
  profileAvatarUrl,
  setProfileAvatarUrl,
  handleSaveProfile,
  isLoginMusicEnabled,
  setIsLoginMusicEnabled,
  loginMusicUrl,
  setLoginMusicUrl,
  fullSizeScreen,
  setFullSizeScreen,
  currentPassword,
  setCurrentPassword,
  newPassword,
  setNewPassword,
  confirmPassword,
  setConfirmPassword,
  isChangingPassword,
  handleSavePassword,
  totpDisableCode,
  setTotpDisableCode,
  handleDisable2FA,
  handleEnable2FA,
  setupData,
  totpError,
  totpCode,
  setTotpCode,
  lockscreenPin,
  setLockscreenPin,
  handleCloseApp,
  weatherInfo,
  pinnedWidgetSides,
  setPinnedWidgetSides,
  collapsedSides,
  setCollapsedSides,
  sideWidths,
  setSideWidths,
  topPanelHeight,
  setTopPanelHeight,
  showToast,
  setPinnedWidgets,
  desktopGridCols,
  setDesktopGridCols,
  desktopIconSize,
  setDesktopIconSize,
  desktopShowLabels,
  setDesktopShowLabels,
}: AppViewportContainerProps) {
  React.useEffect(() => {
    if (typeof window === "undefined") return
    const currentTheme = (theme || (document.documentElement.classList.contains("dark") ? "dark" : "light")) === "dark" ? "dark" : "light"
    const currentLang = lang || localStorage.getItem("language") || "pt-BR"

    const broadcastToIframes = () => {
      const activeAccountWorkspaces = (currentAccount?.workspaces || [])
        .filter((w: any) => w.status !== false)
        .map((w: any) => ({
          id: w.id,
          name: w.name,
          slug: w.slug,
          role: (profileRole === "owner" || profileRole === "admin" ? "admin" : null) || (currentWorkspace?.id === w.id ? workspaceRole : null) || profileRole || "member",
        }))

      const contextPayload = {
        theme: currentTheme,
        language: (currentLang || "pt").split("-")[0],
        user: user ? {
          id: user.id,
          name: user.name,
          email: user.email,
          avatarUrl: user.avatarUrl ?? null,
          status: user.status || "active",
        } : null,
        workspace: currentWorkspace ? {
          id: currentWorkspace.id,
          name: currentWorkspace.name,
          slug: currentWorkspace.slug,
          role: workspaceRole || (profileRole === "owner" || profileRole === "admin" ? "admin" : "member"),
        } : null,
        account: currentAccount ? {
          id: currentAccount.id,
          name: currentAccount.name,
          slug: currentAccount.slug,
          role: profileRole || "member",
          workspaces: activeAccountWorkspaces,
        } : null,
        users: (usersList || []).map((u: any) => ({
          id: u.id,
          name: u.name,
          email: u.email,
          avatarUrl: u.avatarUrl ?? null,
          status: u.status || "active",
          workspaceRole: u.workspaceRole !== undefined ? u.workspaceRole : (profileRole === "owner" || profileRole === "admin" ? "admin" : null),
          accountRole: u.accountRole || u.role || "member",
        })),
      }
      const iframes = document.querySelectorAll("iframe")
      iframes.forEach((iframe) => {
        try {
          if (iframe.contentWindow) {
            iframe.contentWindow.postMessage({ type: "BOLD_THEME_CHANGED", payload: currentTheme, theme: currentTheme }, "*")
            iframe.contentWindow.postMessage({ type: "BOLD_WIDGET_THEME_CHANGED", payload: currentTheme, theme: currentTheme }, "*")
            iframe.contentWindow.postMessage({ type: "BOLD_LANGUAGE_CHANGED", payload: currentLang, language: currentLang, lang: currentLang }, "*")
            iframe.contentWindow.postMessage({ type: "BOLD_WIDGET_LANGUAGE_CHANGED", payload: currentLang, language: currentLang, lang: currentLang }, "*")
            iframe.contentWindow.postMessage({ type: "BOLD_CONTEXT_CHANGED", payload: contextPayload }, "*")
            iframe.contentWindow.postMessage({ type: "BOLD_WIDGET_CONTEXT_CHANGED", payload: contextPayload }, "*")
            iframe.contentWindow.postMessage({ type: "BOLD_CONTEXT_UPDATE", payload: contextPayload }, "*")
            iframe.contentWindow.postMessage({ type: "BOLD_WIDGET_CONTEXT_UPDATE", payload: contextPayload }, "*")
            if (activeAccountWorkspaces.length > 0) {
              iframe.contentWindow.postMessage({ type: "BOLD_WORKSPACES_CHANGED", payload: activeAccountWorkspaces, workspaces: activeAccountWorkspaces }, "*")
            }
            if (contextPayload.users.length > 0) {
              iframe.contentWindow.postMessage({ type: "BOLD_USERS_CHANGED", payload: contextPayload.users, users: contextPayload.users }, "*")
              iframe.contentWindow.postMessage({ type: "BOLD_WIDGET_USERS_CHANGED", payload: contextPayload.users, users: contextPayload.users }, "*")
            }
          }
        } catch (e) {
          // ignore cross-origin error if any
        }
      })
    }

    broadcastToIframes()
    const t1 = setTimeout(broadcastToIframes, 50)
    const t2 = setTimeout(broadcastToIframes, 150)
    const t3 = setTimeout(broadcastToIframes, 300)
    return () => {
      clearTimeout(t1)
      clearTimeout(t2)
      clearTimeout(t3)
    }
  }, [activeAppId, splitActiveAppId, focusedPane, theme, lang, openApps, usersList, profileRole, workspaceRole, currentWorkspace, currentAccount, user])

  const handleSelectAppInTab = (instanceId: string, app: App) => {
    const safeSlug = getAppSlug(app)
    const installed = installedApps.find((ia) => isAppMatch(ia, app.slug || app.id))
    const resolvedAppId = installed?.app?.id || app.id
    const appWithSlug = { ...app, id: resolvedAppId, slug: safeSlug }
    const baseAppUrl = installed?.app?.iframeUrl || app.iframeUrl

    let savedSubpath = ""
    if (typeof window !== "undefined" && currentWorkspace?.id) {
      savedSubpath =
        localStorage.getItem(`bold_subpath_${currentWorkspace.id}_${safeSlug}`) ||
        localStorage.getItem(`bold_subpath_${currentWorkspace.id}_${appWithSlug.id}`) ||
        ""
    }
    if (savedSubpath && !savedSubpath.startsWith("/")) savedSubpath = "/" + savedSubpath
    if (savedSubpath === "/" || savedSubpath === "/blank") savedSubpath = ""

    const finalIframeUrl = savedSubpath ? resolveIframeUrl(baseAppUrl, savedSubpath) : baseAppUrl

    setOpenApps((prev) =>
      prev.map((t) =>
        t.instanceId === instanceId
          ? {
              ...t,
              baseIframeUrl: baseAppUrl,
              subpath: savedSubpath,
              app: {
                ...appWithSlug,
                iframeUrl: finalIframeUrl
              }
            }
          : t
      )
    )

    if (typeof window !== "undefined" && (currentAccount?.id || currentAccount?.slug) && (currentWorkspace?.id || currentWorkspace?.slug)) {
      const fullPath = buildWorkspaceAppPath(currentAccount, currentWorkspace, appWithSlug, savedSubpath)
      if (window.location.pathname + window.location.search + window.location.hash !== fullPath) {
        window.history.replaceState({ path: fullPath }, "", fullPath)
      }
    }
  }

  const handleSelectSettingsInTab = (instanceId: string) => {
    if (setSettingsTab) setSettingsTab(isMobile ? "menu" : "company")
    const settingsApp: App = {
      id: "settings",
      name: "Ajustes do Sistema",
      slug: "settings",
      description: "Configurações globais do sistema",
      iconUrl: "",
      iframeUrl: ""
    }
    setOpenApps((prev) =>
      prev.map((t) =>
        t.instanceId === instanceId
          ? {
              ...t,
              baseIframeUrl: "",
              subpath: "",
              app: settingsApp
            }
          : t
      )
    )
    if (typeof window !== "undefined" && (currentAccount?.id || currentAccount?.slug) && (currentWorkspace?.id || currentWorkspace?.slug)) {
      const fullPath = `${getWorkspaceBasePath(currentAccount, currentWorkspace)}/settings`
      if (window.location.pathname !== fullPath) {
        window.history.replaceState({ path: fullPath }, "", fullPath)
      }
    }
  }

  const handleSelectProfileInTab = (instanceId: string) => {
    const profileApp: App = {
      id: "profile",
      name: "Meu Perfil",
      slug: "profile",
      description: "Gerenciamento de perfil e credenciais",
      iconUrl: "emoji:👤",
      iframeUrl: ""
    }
    setOpenApps((prev) =>
      prev.map((t) =>
        t.instanceId === instanceId
          ? {
              ...t,
              baseIframeUrl: "",
              subpath: "",
              app: profileApp
            }
          : t
      )
    )
    if (typeof window !== "undefined" && (currentAccount?.id || currentAccount?.slug) && (currentWorkspace?.id || currentWorkspace?.slug)) {
      const fullPath = `${getWorkspaceBasePath(currentAccount, currentWorkspace)}/profile`
      if (window.location.pathname !== fullPath) {
        window.history.replaceState({ path: fullPath }, "", fullPath)
      }
    }
  }

  const handleSelectMarketplaceInTab = (instanceId: string) => {
    const marketplaceApp: App = {
      id: "marketplace",
      name: "Loja de Aplicativos",
      slug: "marketplace",
      description: "Catálogo de aplicativos e extensões da plataforma Bold",
      iconUrl: "emoji:🛒",
      iframeUrl: ""
    }
    setOpenApps((prev) =>
      prev.map((t) =>
        t.instanceId === instanceId
          ? {
              ...t,
              baseIframeUrl: "",
              subpath: "",
              app: marketplaceApp
            }
          : t
      )
    )
    if (typeof window !== "undefined" && (currentAccount?.id || currentAccount?.slug) && (currentWorkspace?.id || currentWorkspace?.slug)) {
      const fullPath = `${getWorkspaceBasePath(currentAccount, currentWorkspace)}/marketplace`
      if (window.location.pathname !== fullPath) {
        window.history.replaceState({ path: fullPath }, "", fullPath)
      }
    }
  }

  const isCurrentWorkspaceAdmin =
    profileRole === "admin" ||
    profileRole === "owner" ||
    workspaceRole === "admin" ||
    Boolean(
      currentWorkspace?.id &&
      userWorkspaceMemberships.some((m) => m.workspaceId === currentWorkspace.id && m.role === "admin")
    )

  const renderAppContent = (tab: OpenAppTab) => {
    if (tab.app.slug === "new-tab" || tab.app.id.startsWith("new-tab")) {
      return (
        <NewTabView
          tabInstanceId={tab.instanceId}
          installedApps={installedApps}
          hiddenAppIds={hiddenAppIds}
          onSelectApp={(app) => handleSelectAppInTab(tab.instanceId, app)}
          onSelectSettings={() => handleSelectSettingsInTab(tab.instanceId)}
          onSelectProfile={() => handleSelectProfileInTab(tab.instanceId)}
          onSelectMarketplace={() => handleSelectMarketplaceInTab(tab.instanceId)}
          canAccessMarketplace={isCurrentWorkspaceAdmin}
        />
      )
    }

    if (tab.app.id === "settings") {
      return (
        <div className="flex-1 w-full h-full min-h-0 flex flex-col bg-zinc-50 dark:bg-zinc-950">
          <SettingsView
            isMobile={isMobile}
            settingsTab={settingsTab}
            setSettingsTab={(t: string) => setSettingsTab(t as any)}
            t={t}
            profileRole={profileRole}
            workspaceRole={workspaceRole}
            currentAccount={currentAccount}
            handleSaveCompany={handleSaveCompany}
            companyName={companyName}
            setCompanyName={setCompanyName}
            companyDocument={companyDocument}
            setCompanyDocument={setCompanyDocument}
            companyLogoUrl={companyLogoUrl || ""}
            setCompanyLogoUrl={(val: string) => setCompanyLogoUrl(val)}
            companyBannerUrl={companyBannerUrl || ""}
            setCompanyBannerUrl={(val: string) => setCompanyBannerUrl?.(val)}
            companyPrimaryColor={companyPrimaryColor}
            setCompanyPrimaryColor={setCompanyPrimaryColor}
            companyCustomDomain={companyCustomDomain}
            setCompanyCustomDomain={setCompanyCustomDomain}
            companySamlEnabled={companySamlEnabled}
            setCompanySamlEnabled={setCompanySamlEnabled}
            companySamlIdpEntityId={companySamlIdpEntityId}
            setCompanySamlIdpEntityId={setCompanySamlIdpEntityId}
            companySamlSsoUrl={companySamlSsoUrl}
            setCompanySamlSsoUrl={setCompanySamlSsoUrl}
            companySamlCertificate={companySamlCertificate}
            setCompanySamlCertificate={setCompanySamlCertificate}
            accounts={accounts}
            activeAccountId={currentAccount?.id || ""}
            handleSwitchWorkspace={handleSwitchWorkspace}
            handleCreateWorkspace={handleCreateWorkspace}
            handleUpdateWorkspace={handleUpdateWorkspace}
            handleArchiveWorkspace={handleArchiveWorkspace}
            handleReactivateWorkspace={handleReactivateWorkspace}
            handleDeleteWorkspace={handleDeleteWorkspace}
            usersList={usersList}
            handleInviteUser={handleInviteUser}
            handleUpdateAccountMember={handleUpdateAccountMember}
            handleUpdateUserRole={handleUpdateUserRole}
            handleRemoveUser={handleRemoveUser}
            wallpaperTab={wallpaperTab}
            setWallpaperTab={(t: string) => setWallpaperTab(t as any)}
            mobileWallpaper={mobileWallpaper}
            setMobileWallpaper={setMobileWallpaper}
            desktopWallpaper={desktopWallpaper}
            setDesktopWallpaper={setDesktopWallpaper}
            customWallpapers={customWallpapers}
            setCustomWallpapers={setCustomWallpapers}
            useSeparateMobileWallpaper={useSeparateMobileWallpaper}
            setUseSeparateMobileWallpaper={setUseSeparateMobileWallpaper}
            handleSaveWallpaperToDb={handleSaveWallpaperToDb}
            dockPosition={dockPosition === "top" ? "bottom" : dockPosition}
            setDockPosition={setDockPosition}
            dockSize={dockSize}
            setDockSize={setDockSize}
            dockAutoHide={dockAutoHide}
            setDockAutoHide={setDockAutoHide}
            topBarConfig={topBarConfig}
            setTopBarConfig={setTopBarConfig}
            installedApps={installedApps}
            handleToggleAppStatus={handleToggleAppStatus}
            handleUninstallApp={handleUninstallApp}
            handleInstallApp={handleInstallApp}
            handleRenameApp={handleRenameApp}
            availableWidgets={availableWidgets}
            setWidgetsConfig={setWidgetsConfig}
            pinnedWidgets={pinnedWidgets}
            handleToggleWidgetPin={handleToggleWidgetPin}
            marketplaceApps={marketplaceApps}
            globallyInstalledApps={globallyInstalledApps}
            developerApps={developerApps}
            setAuthAppToShow={setAuthAppToShow}
            handleToggleGlobalApp={handleToggleGlobalApp}
            apiKey={apiKey}
            handleGenerateApiKey={handleGenerateApiKey}
            webhookUrl={webhookUrl}
            setWebhookUrl={setWebhookUrl}
            handleSaveWebhook={handleSaveWebhook}
            handleOpenMarketplaceTab={handleOpenMarketplaceTab}
            dockAppIds={dockAppIds}
            setDockAppIds={setDockAppIds}
            hiddenAppIds={hiddenAppIds}
            setHiddenAppIds={setHiddenAppIds}
            devSubTab={devSubTab}
            setDevSubTab={setDevSubTab}
            desktopGridCols={desktopGridCols}
            setDesktopGridCols={setDesktopGridCols}
            desktopIconSize={desktopIconSize}
            setDesktopIconSize={setDesktopIconSize}
            desktopShowLabels={desktopShowLabels}
            setDesktopShowLabels={setDesktopShowLabels}
            lang={lang}
            user={user}
            handleOpenApp={handleOpenApp}
          />
        </div>
      )
    }

    if (tab.app.id === "profile") {
      return (
        <div className="flex-1 w-full overflow-hidden bg-zinc-50 dark:bg-zinc-950">
          <ProfileView
            isMobile={isMobile}
            profileTab={profileTab}
            setProfileTab={(t: string) => setProfileTab(t as any)}
            t={t}
            user={user}
            profileName={profileName}
            setProfileName={setProfileName}
            profilePhone={profilePhone}
            setProfilePhone={setProfilePhone}
            profileAvatarUrl={profileAvatarUrl}
            setProfileAvatarUrl={setProfileAvatarUrl}
            handleSaveProfile={handleSaveProfile}
            isLoginMusicEnabled={isLoginMusicEnabled}
            setIsLoginMusicEnabled={setIsLoginMusicEnabled}
            loginMusicUrl={loginMusicUrl}
            setLoginMusicUrl={setLoginMusicUrl}
            fullSizeScreen={fullSizeScreen}
            setFullSizeScreen={setFullSizeScreen}
            lang={lang}
            currentPassword={currentPassword}
            setCurrentPassword={setCurrentPassword}
            newPassword={newPassword}
            setNewPassword={setNewPassword}
            confirmPassword={confirmPassword}
            setConfirmPassword={setConfirmPassword}
            isChangingPassword={isChangingPassword}
            handleSavePassword={handleSavePassword}
            totpDisableCode={totpDisableCode}
            setTotpDisableCode={setTotpDisableCode}
            handleDisable2FA={(e) => handleDisable2FA(e as any)}
            handleEnable2FA={(e) => handleEnable2FA(e as any)}
            setupData={setupData}
            totpError={totpError}
            totpCode={totpCode}
            setTotpCode={setTotpCode}
            lockscreenPin={lockscreenPin}
            setLockscreenPin={setLockscreenPin}
            handleCloseApp={handleCloseApp}
          />
        </div>
      )
    }

    if (tab.app.id === "marketplace") {
      if (!isCurrentWorkspaceAdmin) {
        return (
          <div className="flex-1 w-full h-full flex flex-col items-center justify-center bg-zinc-50 dark:bg-zinc-950 p-6 font-sans text-center">
            <div className="max-w-md w-full bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl p-6 shadow-sm space-y-2">
              <h3 className="text-sm font-bold text-zinc-900 dark:text-zinc-100">Acesso Restrito</h3>
              <p className="text-xs text-zinc-500 dark:text-zinc-400">
                Apenas administradores deste Workspace têm permissão para acessar a Loja de Aplicativos.
              </p>
            </div>
          </div>
        )
      }
      return (
        <div className="flex-1 w-full overflow-hidden bg-zinc-50 dark:bg-zinc-950">
          <MarketplaceView
            t={t}
            lang={lang}
            marketplaceApps={marketplaceApps}
            globallyInstalledApps={globallyInstalledApps}
            developerApps={developerApps}
            installedApps={installedApps}
            widgetsConfig={availableWidgets}
            setWidgetsConfig={setWidgetsConfig}
            setAuthAppToShow={setAuthAppToShow}
            handleToggleGlobalApp={handleToggleGlobalApp}
            handleConfigureAppWorkspaces={handleConfigureAppWorkspaces}
            handleToggleGlobalWidget={handleToggleGlobalWidget}
            handleOpenApp={handleOpenApp}
            workspaces={currentAccount?.workspaces || []}
            adminWorkspaces={adminWorkspaces}
            currentWorkspace={currentWorkspace}
            currentAccount={currentAccount}
            accounts={accounts}
            user={user}
            profileRole={profileRole}
            workspaceRole={workspaceRole}
            userWorkspaceMemberships={userWorkspaceMemberships}
            onAddCustomApp={onAddCustomApp}
          />
        </div>
      )
    }

    if (tab.app.id === "system-weather") {
      return (
        <div className="flex-1 w-full overflow-hidden bg-zinc-50 dark:bg-zinc-950 p-6 flex flex-col items-center justify-center font-sans">
          <div className="max-w-md w-full bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-3xl p-6 shadow-xl space-y-4">
            <WeatherWidget weatherInfo={weatherInfo} lang={lang} />
          </div>
        </div>
      )
    }

    if (tab.app.id === "system-calendar") {
      return (
        <CalendarApp
          calendarDate={calendarDate}
          setCalendarDate={setCalendarDate}
        />
      )
    }

    if (tab.app.id === "system-notes") {
      return (
        <NotesApp
          notesContent={notesContent}
          setNotesContent={setNotesContent}
        />
      )
    }

    if (tab.app.id === "system-calculator") {
      return (
        <CalculatorApp
          calcFormula={calcFormula}
          calcDisplay={calcDisplay}
          handleCalcPress={handleCalcPress}
        />
      )
    }

    return (
      <iframe
        key={iframeRefreshKeys[tab.instanceId] || 0}
        src={tab.app.iframeUrl}
        title={tab.app.name}
        data-instance-id={tab.instanceId}
        allow="camera *; microphone *; geolocation *; fullscreen *; display-capture *; autoplay *; clipboard-read *; clipboard-write *; web-share; downloads; downloads-without-user-activation"
        className="flex-1 w-full border-none bg-white dark:bg-black"
        onLoad={(e) => {
          try {
            const currentTheme = (theme || (typeof window !== "undefined" && document.documentElement.classList.contains("dark") ? "dark" : "light")) === "dark" ? "dark" : "light"
            const currentLang = lang || (typeof window !== "undefined" ? localStorage.getItem("language") || "pt-BR" : "pt-BR")
            const iframeEl = e.currentTarget
            const cw = iframeEl.contentWindow
            if (cw) {
              try {
                const iframeBaseUrl = tab.baseIframeUrl || tab.app.iframeUrl
                const resolveFromIframe = (rawTarget: string) => {
                  try {
                    return new URL(rawTarget, cw.location?.href || iframeBaseUrl).href
                  } catch {
                    return rawTarget
                  }
                }
                const openFromIframe = (rawTarget: string, opts?: { inBackground?: boolean; openInSplit?: boolean }) => {
                  const resolved = resolveFromIframe(rawTarget)
                  handleOpenExternalUrl?.(resolved, {
                    forceNewTab: true,
                    sourceBaseUrl: iframeBaseUrl,
                    ...opts
                  })
                }

                const createWindowShim = () => {
                  const locProxy = {
                    _href: "",
                    get href() {
                      return this._href
                    },
                    set href(val: string) {
                      this._href = val
                      if (val && val !== "about:blank") openFromIframe(String(val))
                    },
                    assign(val: string) {
                      if (val && val !== "about:blank") openFromIframe(String(val))
                    },
                    replace(val: string) {
                      if (val && val !== "about:blank") openFromIframe(String(val))
                    }
                  }
                  const shim = {
                    closed: false,
                    focus() {},
                    close() { shim.closed = true },
                    postMessage() {},
                    location: locProxy
                  }
                  return shim as unknown as Window
                }

                cw.open = (url?: string | URL, _target?: string, _features?: string) => {
                  const strUrl = url != null ? String(url).trim() : ""
                  if (strUrl && strUrl !== "about:blank") {
                    openFromIframe(strUrl)
                  }
                  return createWindowShim()
                }

                cw.document.addEventListener("click", (ev: MouseEvent) => {
                  const anchor = (ev.target as HTMLElement)?.closest("a")
                  if (!anchor) return
                  const rawHref = anchor.getAttribute("href")
                  if (!rawHref || rawHref.startsWith("javascript:") || rawHref.startsWith("#") || rawHref.startsWith("mailto:") || rawHref.startsWith("tel:")) return
                  const fullHref = anchor.href || resolveFromIframe(rawHref)
                  const targetAttr = (anchor.getAttribute("target") || "").toLowerCase()
                  const isNewTarget = targetAttr === "_blank" || targetAttr === "_top" || targetAttr === "_parent"
                  const hasModifier = ev.ctrlKey || ev.metaKey || ev.shiftKey
                  let isDifferentOrigin = false
                  try {
                    isDifferentOrigin = new URL(fullHref).origin !== cw.location.origin
                  } catch {}

                  if (isNewTarget || hasModifier || isDifferentOrigin) {
                    ev.preventDefault()
                    ev.stopPropagation()
                    openFromIframe(fullHref)
                  }
                }, true)

                cw.document.addEventListener("mousedown", (ev: MouseEvent) => {
                  if (ev.button !== 1) return
                  const anchor = (ev.target as HTMLElement)?.closest("a")
                  if (!anchor) return
                  const rawHref = anchor.getAttribute("href")
                  if (!rawHref || rawHref.startsWith("javascript:") || rawHref.startsWith("#")) return
                  ev.preventDefault()
                }, true)

                cw.document.addEventListener("auxclick", (ev: MouseEvent) => {
                  if (ev.button !== 1) return
                  const anchor = (ev.target as HTMLElement)?.closest("a")
                  if (!anchor) return
                  const rawHref = anchor.getAttribute("href")
                  if (!rawHref || rawHref.startsWith("javascript:") || rawHref.startsWith("#") || rawHref.startsWith("mailto:") || rawHref.startsWith("tel:")) return
                  const fullHref = anchor.href || resolveFromIframe(rawHref)
                  ev.preventDefault()
                  ev.stopPropagation()
                  openFromIframe(fullHref)
                }, true)

                cw.document.addEventListener("contextmenu", (ev: MouseEvent) => {
                  const anchor = (ev.target as HTMLElement)?.closest("a")
                  if (!anchor) return
                  const rawHref = anchor.getAttribute("href")
                  if (!rawHref || rawHref.startsWith("javascript:") || rawHref.startsWith("#") || rawHref.startsWith("mailto:") || rawHref.startsWith("tel:")) return
                  const fullHref = anchor.href || resolveFromIframe(rawHref)
                  ev.preventDefault()
                  ev.stopPropagation()
                  const rect = iframeEl.getBoundingClientRect()
                  window.dispatchEvent(
                    new CustomEvent("bold:link-context-menu", {
                      detail: {
                        url: fullHref,
                        text: (anchor.textContent || "").trim(),
                        x: rect.left + ev.clientX,
                        y: rect.top + ev.clientY,
                        sourceBaseUrl: iframeBaseUrl
                      }
                    })
                  )
                }, true)
              } catch (e) {}

              const activeAccountWorkspaces = (currentAccount?.workspaces || [])
                .filter((w: any) => w.status !== false)
                .map((w: any) => ({
                  id: w.id,
                  name: w.name,
                  slug: w.slug,
                  role: (profileRole === "owner" || profileRole === "admin" ? "admin" : null) || (currentWorkspace?.id === w.id ? workspaceRole : null) || profileRole || "member",
                }))

              const contextPayload = {
                theme: currentTheme,
                language: (currentLang || "pt").split("-")[0],
                user: user ? {
                  id: user.id,
                  name: user.name,
                  email: user.email,
                  avatarUrl: user.avatarUrl ?? null,
                  status: user.status || "active",
                } : null,
                workspace: currentWorkspace ? {
                  id: currentWorkspace.id,
                  name: currentWorkspace.name,
                  slug: currentWorkspace.slug,
                  role: workspaceRole || (profileRole === "owner" || profileRole === "admin" ? "admin" : "member"),
                } : null,
                account: currentAccount ? {
                  id: currentAccount.id,
                  name: currentAccount.name,
                  slug: currentAccount.slug,
                  role: profileRole || "member",
                  workspaces: activeAccountWorkspaces,
                } : null,
                users: (usersList || []).map((u: any) => ({
                  id: u.id,
                  name: u.name,
                  email: u.email,
                  avatarUrl: u.avatarUrl ?? null,
                  status: u.status || "active",
                  workspaceRole: u.workspaceRole !== undefined ? u.workspaceRole : (profileRole === "owner" || profileRole === "admin" ? "admin" : null),
                  accountRole: u.accountRole || u.role || "member",
                })),
              }

              cw.postMessage({ type: "BOLD_THEME_CHANGED", payload: currentTheme, theme: currentTheme }, "*")
              cw.postMessage({ type: "BOLD_WIDGET_THEME_CHANGED", payload: currentTheme, theme: currentTheme }, "*")
              cw.postMessage({ type: "BOLD_LANGUAGE_CHANGED", payload: (currentLang || "pt").split("-")[0], language: (currentLang || "pt").split("-")[0], lang: (currentLang || "pt").split("-")[0] }, "*")
              cw.postMessage({ type: "BOLD_WIDGET_LANGUAGE_CHANGED", payload: (currentLang || "pt").split("-")[0], language: (currentLang || "pt").split("-")[0], lang: (currentLang || "pt").split("-")[0] }, "*")
              cw.postMessage({ type: "BOLD_CONTEXT_CHANGED", payload: contextPayload }, "*")
              cw.postMessage({ type: "BOLD_WIDGET_CONTEXT_CHANGED", payload: contextPayload }, "*")
              cw.postMessage({ type: "BOLD_CONTEXT_UPDATE", payload: contextPayload }, "*")
              cw.postMessage({ type: "BOLD_WIDGET_CONTEXT_UPDATE", payload: contextPayload }, "*")
              if (activeAccountWorkspaces.length > 0) {
                cw.postMessage({ type: "BOLD_WORKSPACES_CHANGED", payload: activeAccountWorkspaces, workspaces: activeAccountWorkspaces }, "*")
              }
              if (contextPayload.users.length > 0) {
                cw.postMessage({ type: "BOLD_USERS_CHANGED", payload: contextPayload.users, users: contextPayload.users }, "*")
                cw.postMessage({ type: "BOLD_WIDGET_USERS_CHANGED", payload: contextPayload.users, users: contextPayload.users }, "*")
              }
              cw.postMessage({ type: "BOLD_GET_ROUTE", requestId: "onload" }, "*")
              cw.postMessage({ type: "BOLD_REQUEST_ROUTE" }, "*")

              try {
                const loc = cw.location
                if (loc && loc.pathname && !loc.href.startsWith("about:") && loc.pathname !== "blank") {
                  let sub = loc.pathname
                  if (!sub.startsWith("/")) sub = "/" + sub
                  if (sub === "/" || sub === "/blank") sub = ""
                  const fullSub = `${sub}${loc.search || ""}${loc.hash || ""}`
                  if (fullSub && updateAppSubpath) {
                    updateAppSubpath(tab.instanceId, fullSub)
                  }
                }
              } catch (locErr) {
                // Cross-origin iframe security restriction
              }
            }
          } catch (err) {
            // ignore cross-origin error
          }
        }}
      />
    )
  }

  const renderIframePane = (instanceId: string, panePosition: "left" | "right") => {
    const tab = openApps.find((t) => t.instanceId === instanceId)
    if (!tab) return null

    const isFocused = focusedPane === panePosition

    return (
      <div
        data-tab-container={instanceId}
        className={`w-full h-full flex flex-col relative transition-all ${isFocused ? "ring-2 ring-[#004EB1]/40 dark:ring-blue-600/40 z-10" : "opacity-90"
          }`}
        onClick={() => setFocusedPane(panePosition)}
      >
        {renderAppContent(tab)}
      </div>
    )
  }

  const renderSelectorPane = () => {
    const otherOpenTabs = openApps.filter((t) => t.instanceId !== activeAppId)

    return (
      <div
        className="w-full h-full flex flex-col bg-zinc-50 dark:bg-zinc-950 p-6 font-sans overflow-y-auto"
        onClick={() => setFocusedPane("right")}
      >
        <div className="max-w-md mx-auto w-full flex flex-col gap-6 my-auto">
          <div className="text-center">
            <h3 className="text-sm font-bold text-zinc-850 dark:text-zinc-100 flex items-center justify-center gap-2">
              <Split className="size-4 text-blue-500 dark:text-blue-400" />
              Dividir Visualização
            </h3>
            <p className="text-[10px] text-zinc-500 mt-1">
              Escolha um aplicativo para abrir no Painel 2
            </p>
          </div>

          {otherOpenTabs.length > 0 && (
            <div className="flex flex-col gap-2">
              <h4 className="text-[9px] font-bold text-zinc-400 uppercase tracking-wider">Guias Abertas</h4>
              <div className="grid grid-cols-1 gap-1.5">
                {otherOpenTabs.map((t) => (
                  <button
                    key={t.instanceId}
                    onClick={() => {
                      setSplitActiveAppId(t.instanceId)
                      setFocusedPane("right")
                    }}
                    className="flex items-center gap-3 px-3 py-2 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg hover:border-blue-500 dark:hover:border-blue-500 text-left transition-all group cursor-pointer shadow-xs"
                  >
                    <AppIcon iconUrl={t.app.iconUrl} iframeUrl={t.app.iframeUrl} name={t.app.name} size="small" />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold text-zinc-800 dark:text-zinc-200 truncate">{t.app.name}</div>
                      <div className="text-[9px] text-zinc-400 truncate">{t.app.iframeUrl}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="flex flex-col gap-2">
            <h4 className="text-[9px] font-bold text-zinc-400 uppercase tracking-wider">Abrir Novo Aplicativo</h4>
            <div className="grid grid-cols-2 gap-2">
              {installedApps.filter(ia => ia.status === "active").map(({ app }) => (
                <button
                  key={app.id}
                  onClick={() => {
                    const newInstanceId = `${app.id}-${Math.random().toString(36).substring(2, 9)}`
                    setOpenApps((prev) => [...prev, {
                      instanceId: newInstanceId,
                      app,
                      pinned: false,
                      groupId: null
                    }])
                    setSplitActiveAppId(newInstanceId)
                    setFocusedPane("right")
                  }}
                  className="flex flex-col items-center gap-2 p-3 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg hover:border-blue-500 dark:hover:border-blue-500 transition-all group cursor-pointer shadow-xs"
                >
                  <AppIcon iconUrl={app.iconUrl} iframeUrl={app.iframeUrl} name={app.name} size="medium" />
                  <span className="text-[11px] font-bold text-zinc-800 dark:text-zinc-200 text-center truncate w-full">{app.name}</span>
                </button>
              ))}
            </div>
          </div>

          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setSplitActiveAppId(null)}
            className="text-[11px] text-zinc-500 hover:text-zinc-700 mx-auto"
          >
            Cancelar Divisão de Tela
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div
      className={`flex-1 w-full h-full flex flex-col relative ${isMobile ? "pb-14 md:pb-0" : ""}`}
      style={{ display: activeAppId !== null ? "flex" : "none" }}
    >
      {/* CONTROL BAR DO SPLIT SCREEN (Apenas quando splitActiveAppId !== null) */}
      {splitActiveAppId !== null && (
        <div className="flex h-9 w-full items-center justify-between border-b border-zinc-200 bg-zinc-100 px-4 dark:border-zinc-800 dark:bg-zinc-900 shrink-0">
          <div className="flex items-center gap-2">
            <Split className="size-3.5 text-blue-550 dark:text-blue-400" />
            <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-550 dark:text-zinc-400">Modo Tela Dividida</span>
          </div>
          <div className="flex items-center gap-4">
            {/* Orientação */}
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] text-zinc-450 dark:text-zinc-400">Orientação:</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-6 px-1.5 text-[10px] text-zinc-650 hover:bg-zinc-200 dark:text-zinc-300 dark:hover:bg-zinc-800 flex items-center gap-1"
                onClick={() => setSplitOrientation(splitOrientation === "horizontal" ? "vertical" : "horizontal")}
              >
                {splitOrientation === "horizontal" ? (
                  <>
                    <Rows className="size-3" />
                    <span>Vertical</span>
                  </>
                ) : (
                  <>
                    <Columns className="size-3" />
                    <span>Horizontal</span>
                  </>
                )}
              </Button>
            </div>

            {/* Proporções */}
            <div className="flex items-center gap-1">
              <span className="text-[10px] text-zinc-450 dark:text-zinc-400 mr-1">Proporção:</span>
              <button
                type="button"
                onClick={() => setSplitProportion(50)}
                className={`px-1.5 py-0.5 text-[9px] font-bold rounded-sm border transition-colors ${splitProportion === 50
                    ? "bg-blue-550 border-blue-550 text-white dark:bg-blue-600 dark:border-blue-600"
                    : "border-zinc-200 bg-white hover:bg-zinc-50 text-zinc-600 dark:border-zinc-800 dark:bg-zinc-900 dark:hover:bg-zinc-800 dark:text-zinc-300 cursor-pointer"
                  }`}
              >
                50:50
              </button>
              <button
                type="button"
                onClick={() => setSplitProportion(30)}
                className={`px-1.5 py-0.5 text-[9px] font-bold rounded-sm border transition-colors ${splitProportion === 30
                    ? "bg-blue-550 border-blue-550 text-white dark:bg-blue-600 dark:border-blue-600"
                    : "border-zinc-200 bg-white hover:bg-zinc-50 text-zinc-600 dark:border-zinc-800 dark:bg-zinc-900 dark:hover:bg-zinc-800 dark:text-zinc-300 cursor-pointer"
                  }`}
              >
                30:70
              </button>
              <button
                type="button"
                onClick={() => setSplitProportion(70)}
                className={`px-1.5 py-0.5 text-[9px] font-bold rounded-sm border transition-colors ${splitProportion === 70
                    ? "bg-blue-550 border-blue-550 text-white dark:bg-blue-600 dark:border-blue-600"
                    : "border-zinc-200 bg-white hover:bg-zinc-50 text-zinc-600 dark:border-zinc-800 dark:bg-zinc-900 dark:hover:bg-zinc-800 dark:text-zinc-300 cursor-pointer"
                  }`}
              >
                70:30
              </button>
            </div>

            {/* Sair do Split */}
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-6 px-2 text-[10px] border-zinc-250 dark:border-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-800 text-zinc-600 dark:text-zinc-400"
              onClick={() => setSplitActiveAppId(null)}
            >
              Fechar Divisão
            </Button>
          </div>
        </div>
      )}

      {/* ÁREA DE EXIBIÇÃO: SPLIT OU UNITÁRIA + PINNED SIDEBARS (FLEX INTEGRADO) */}
      {(() => {
        const effectivePinnedWidgets = Array.from(new Set([...(pinnedWidgets || []), ...(activeFloatingWidgets || [])]))

        return (
          <div className="flex flex-col flex-1 w-full h-full min-h-0 overflow-hidden">
            {/* Top Pinned Panel Inline */}
            <PinnedWidgetsPanel
              side="top"
              pinnedWidgets={effectivePinnedWidgets}
              widgetsConfig={widgetsConfig}
              minimizedWidgets={minimizedWidgets}
              calcFocusedWidgetId={calcFocusedWidgetId}
              setCalcFocusedWidgetId={setCalcFocusedWidgetId}
              handleMinimizeWidget={handleMinimizeWidget}
              handlePinWidget={handlePinWidget}
              handleToggleFloatingWidget={handleToggleFloatingWidget}
              calendarDate={calendarDate}
              setCalendarDate={setCalendarDate}
              weatherInfo={weatherInfo}
              lang={lang}
              theme={theme}
              calcFormula={calcFormula}
              setCalcFormula={setCalcFormula}
              calcDisplay={calcDisplay}
              setCalcDisplay={setCalcDisplay}
              showCalcHistory={showCalcHistory}
              setShowCalcHistory={setShowCalcHistory}
              calcHistory={calcHistory}
              setCalcHistory={setCalcHistory}
              setCalcResetOnNext={setCalcResetOnNext}
              handleCalcPress={handleCalcPress}
              notesContent={notesContent}
              setNotesContent={setNotesContent}
              pinnedWidgetSides={pinnedWidgetSides}
              setPinnedWidgetSides={setPinnedWidgetSides}
              collapsedSides={collapsedSides}
              setCollapsedSides={setCollapsedSides}
              sideWidths={sideWidths}
              setSideWidths={setSideWidths}
              topPanelHeight={topPanelHeight}
              setTopPanelHeight={setTopPanelHeight}
              showToast={showToast}
              setPinnedWidgets={setPinnedWidgets}
            />

            <div className="flex flex-1 w-full h-full min-h-0 overflow-hidden">
              {/* Left Pinned Sidebar Inline */}
              <PinnedWidgetsPanel
                side="left"
                pinnedWidgets={effectivePinnedWidgets}
                widgetsConfig={widgetsConfig}
                minimizedWidgets={minimizedWidgets}
                calcFocusedWidgetId={calcFocusedWidgetId}
                setCalcFocusedWidgetId={setCalcFocusedWidgetId}
                handleMinimizeWidget={handleMinimizeWidget}
                handlePinWidget={handlePinWidget}
                handleToggleFloatingWidget={handleToggleFloatingWidget}
                calendarDate={calendarDate}
                setCalendarDate={setCalendarDate}
                weatherInfo={weatherInfo}
                lang={lang}
                calcFormula={calcFormula}
                setCalcFormula={setCalcFormula}
                calcDisplay={calcDisplay}
                setCalcDisplay={setCalcDisplay}
                showCalcHistory={showCalcHistory}
                setShowCalcHistory={setShowCalcHistory}
                calcHistory={calcHistory}
                setCalcHistory={setCalcHistory}
                setCalcResetOnNext={setCalcResetOnNext}
                handleCalcPress={handleCalcPress}
                notesContent={notesContent}
                setNotesContent={setNotesContent}
                pinnedWidgetSides={pinnedWidgetSides}
                setPinnedWidgetSides={setPinnedWidgetSides}
                collapsedSides={collapsedSides}
                setCollapsedSides={setCollapsedSides}
                sideWidths={sideWidths}
                setSideWidths={setSideWidths}
                topPanelHeight={topPanelHeight}
                setTopPanelHeight={setTopPanelHeight}
                showToast={showToast}
                setPinnedWidgets={setPinnedWidgets}
              />

              {/* Main content */}
              <div className="flex-1 h-full relative overflow-hidden">
                {splitActiveAppId === null ? (
                  openApps.map((tab) => (
                    <div
                      key={tab.instanceId}
                      data-tab-container={tab.instanceId}
                      className="w-full h-full flex flex-col bg-white dark:bg-black"
                      style={{ display: tab.instanceId === activeAppId ? "flex" : "none" }}
                    >
                      {renderAppContent(tab)}
                    </div>
                  ))
                ) : (
                  <ResizablePanelGroup
                    key={`${splitProportion}-${splitOrientation}`}
                    orientation={splitOrientation}
                    className="w-full h-full min-h-[200px]"
                  >
                    <ResizablePanel
                      defaultSize={splitProportion}
                      minSize={20}
                      className="h-full w-full"
                    >
                      {renderIframePane(activeAppId || "", "left")}
                    </ResizablePanel>
                    <ResizableHandle
                      withHandle
                      className={`bg-zinc-300 dark:bg-zinc-700 transition-colors ${splitOrientation === "horizontal" ? "my-1.5" : "mx-1.5"
                        }`}
                    />
                    <ResizablePanel
                      defaultSize={100 - splitProportion}
                      minSize={20}
                      className="h-full w-full"
                    >
                      {splitActiveAppId === "empty"
                        ? renderSelectorPane()
                        : renderIframePane(splitActiveAppId, "right")}
                    </ResizablePanel>
                  </ResizablePanelGroup>
                )}
              </div>

              {/* Right Pinned Sidebar Inline */}
              <PinnedWidgetsPanel
                side="right"
                pinnedWidgets={effectivePinnedWidgets}
                widgetsConfig={widgetsConfig}
                minimizedWidgets={minimizedWidgets}
                calcFocusedWidgetId={calcFocusedWidgetId}
                setCalcFocusedWidgetId={setCalcFocusedWidgetId}
                handleMinimizeWidget={handleMinimizeWidget}
                handlePinWidget={handlePinWidget}
                handleToggleFloatingWidget={handleToggleFloatingWidget}
                calendarDate={calendarDate}
                setCalendarDate={setCalendarDate}
                weatherInfo={weatherInfo}
                lang={lang}
                calcFormula={calcFormula}
                setCalcFormula={setCalcFormula}
                calcDisplay={calcDisplay}
                setCalcDisplay={setCalcDisplay}
                showCalcHistory={showCalcHistory}
                setShowCalcHistory={setShowCalcHistory}
                calcHistory={calcHistory}
                setCalcHistory={setCalcHistory}
                setCalcResetOnNext={setCalcResetOnNext}
                handleCalcPress={handleCalcPress}
                notesContent={notesContent}
                setNotesContent={setNotesContent}
                pinnedWidgetSides={pinnedWidgetSides}
                setPinnedWidgetSides={setPinnedWidgetSides}
                collapsedSides={collapsedSides}
                setCollapsedSides={setCollapsedSides}
                sideWidths={sideWidths}
                setSideWidths={setSideWidths}
                topPanelHeight={topPanelHeight}
                setTopPanelHeight={setTopPanelHeight}
                showToast={showToast}
                setPinnedWidgets={setPinnedWidgets}
              />
            </div>
          </div>
        )
      })()}
    </div>
  )
}
